/**
 * The screens' parts of the voice extras (2026-11-01, Discord's): the
 * priority-speaker badge, the voice channel status (the sidebar's line and
 * the room header's, with its editor), Settings › Voice's noise suppression
 * and priority-speaker key, and Server settings' AFK channel and timeout.
 * Every person-written text keeps its own direction (`dir="auto"`, <bdi>).
 */
import {
  AFK_TIMEOUTS,
  isVoiceChannelType,
  Permissions,
  VOICE_STATUS_MAX,
  type Guild,
  type NoiseSuppression,
  type Snowflake,
} from '@neosocial/protocol';
import { useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { runAction } from '../mod/actions';
import { flagOn, useFlag } from '../store/config';
import { updateData, useData, useDataShallow } from '../store/data';
import { can, channelPermissions } from '../store/selectors';
import { setVoiceStatus, useChannelStatus } from '../store/voiceStatus';
import { Btn, Icon, Seg, Select } from '../ui/kit';
import { openPopover, toast } from '../ui/overlay';
import { SetGroup, SetRow } from '../settings/Settings';
import { KeyCapture } from '../settings/KeyCapture';
import { applyNoiseSuppression, runningNoiseSuppression, setupInput } from './controller';
import { prioritySpeaking } from './machine';
import { effectiveNoise, strongSupported } from './noise';
import { useVoice } from './store';

/* ---------------------------- priority speaker ---------------------------- */

/** The priority-speaker mark beside a person in voice while they talk as one (Discord's). */
export function PriorityBadge({ userId, cls }: { userId: Snowflake; cls?: string }) {
  const { t } = useT();
  const meId = useData((d) => d.me?.id);
  const on = useVoice(
    (v) => v.session.status === 'connected' && prioritySpeaking(v.session, userId, meId),
  );
  if (!on) return null;
  return (
    <span
      className={'vx-prio' + (cls ? ' ' + cls : '')}
      role="img"
      aria-label={t('vx.prio.on')}
      data-tip={t('vx.prio.on')}
      data-priority=""
    >
      <Icon name="megaphone" size="s" />
    </span>
  );
}

/* --------------------------- voice channel status --------------------------- */

/** The sidebar's status line under a voice channel's name (Discord's). */
export function VoiceStatusLine({ channelId }: { channelId: Snowflake }) {
  const status = useChannelStatus(channelId);
  if (!status) return null;
  return (
    <p className="vc-status" data-voice-status="" title={status}>
      <bdi dir="auto">{status}</bdi>
    </p>
  );
}

/** May you set this channel's status: in it, with Set Voice Channel Status there, the flag on. */
export function useCanSetStatus(channelId: Snowflake): boolean {
  const here = useVoice(
    (v) => v.session.channelId === channelId && v.session.status === 'connected',
  );
  const allowed = useData((d) => {
    const c = d.channels[channelId];
    return (
      !!c &&
      isVoiceChannelType(c.type) &&
      can(channelPermissions(d, c), Permissions.SET_VOICE_CHANNEL_STATUS)
    );
  });
  const flag = useFlag('voice_channel_status');
  return here && allowed && flag;
}

function StatusEditor({
  guildId,
  channelId,
  initial,
  close,
}: {
  guildId: Snowflake;
  channelId: Snowflake;
  initial: string;
  close: () => void;
}) {
  const { t, fmt } = useT();
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const n = [...value.trim()].length;
  const tooLong = n > VOICE_STATUS_MAX;
  const save = async (status: string | null) => {
    if (busy || (status !== null && tooLong)) return;
    setBusy(true);
    const r = await runAction(() => setVoiceStatus(guildId, channelId, status));
    setBusy(false);
    if (r.ok) close();
  };
  return (
    <form
      className="vx-st-ed"
      data-status-editor=""
      onSubmit={(e) => {
        e.preventDefault();
        void save(value.trim() ? value : null);
      }}
    >
      <label className="vx-st-lab">
        <span className="ns-label">{t('vx.st.label')}</span>
        <input
          className="nx-input"
          dir="auto"
          value={value}
          maxLength={VOICE_STATUS_MAX * 2}
          placeholder={t('vx.st.placeholder')}
          aria-invalid={tooLong || undefined}
          data-autofocus
          onChange={(e) => setValue(e.target.value)}
        />
      </label>
      <div className="vx-st-foot">
        <span className={'vx-st-n nx-num' + (tooLong ? ' over' : '')} aria-live="polite">
          {t('vx.st.count', { n: fmt.num(n), max: fmt.num(VOICE_STATUS_MAX) })}
        </span>
        {initial ? (
          <Btn size="sm" kind="quiet" disabled={busy} onClick={() => void save(null)}>
            {t('vx.st.clear')}
          </Btn>
        ) : null}
        <Btn size="sm" kind="ghost" onClick={close}>
          {t('vx.st.cancel')}
        </Btn>
        <Btn size="sm" kind="primary" type="submit" disabled={busy || tooLong}>
          {t('vx.st.save')}
        </Btn>
      </div>
    </form>
  );
}

/**
 * The room header's status (Discord's): the text, and for someone who may
 * set it a button that opens the editor ("Set a channel status" while none).
 */
export function RoomStatus({ channelId }: { channelId: Snowflake }) {
  const { t } = useT();
  const status = useChannelStatus(channelId);
  const canSet = useCanSetStatus(channelId);
  const guildId = useData((d) => d.channels[channelId]?.guild_id ?? null);
  if (!status && !canSet) return null;
  const text = status ? <bdi dir="auto">{status}</bdi> : <span>{t('vx.st.set')}</span>;
  if (!canSet || !guildId)
    return (
      <span className="vx-st" data-room-status="" title={status ?? undefined}>
        <span className="vx-st-t">{text}</span>
      </span>
    );
  return (
    <button
      type="button"
      className={'vx-st can' + (status ? '' : ' empty')}
      data-room-status=""
      aria-label={status ? `${t('vx.st.edit')}: ${status}` : t('vx.st.set')}
      title={status ?? undefined}
      onClick={(e) =>
        openPopover(
          e.currentTarget,
          (close) => (
            <StatusEditor
              guildId={guildId}
              channelId={channelId}
              initial={status ?? ''}
              close={close}
            />
          ),
          { placement: 'bottom-start', cls: 'vx-st-pop', label: t('vx.st.label'), width: 400 },
        )
      }
    >
      <Icon name="edit" size="s" />
      <span className="vx-st-t">{text}</span>
    </button>
  );
}

/* ------------------------- Settings › Voice ------------------------- */

/** Noise suppression, as Discord's: Off, Standard (the browser's) or Strong (RNNoise). */
export function NoiseSuppressionGroup() {
  const { t } = useT();
  const mode = useVoice((v) => v.prefs.noiseSuppression);
  const setPrefs = useVoice((v) => v.setPrefs);
  const flag = useFlag('noise_suppression');
  const supported = strongSupported();
  const [running, setRunning] = useState<NoiseSuppression | null>(() => runningNoiseSuppression());
  const note =
    mode !== 'strong'
      ? null
      : !flag
        ? t('vx.ns.strongOff')
        : !supported
          ? t('vx.ns.unsupported')
          : running === 'standard'
            ? t('vx.ns.fellBack')
            : null;
  return (
    <SetGroup title={t('vx.ns.title')}>
      <SetRow label={t('vx.ns.title')} sub={note ?? t(`vx.ns.sub.${mode}`)}>
        <Seg<NoiseSuppression>
          label={t('vx.ns.title')}
          value={mode}
          onChange={(v) => {
            setPrefs({ noiseSuppression: v });
            void applyNoiseSuppression().then((r) => setRunning(r));
          }}
          options={[
            { value: 'off', label: t('vx.ns.off') },
            { value: 'standard', label: t('vx.ns.standard') },
            { value: 'strong', label: t('vx.ns.strong'), icon: 'sparkles' },
          ]}
        />
      </SetRow>
    </SetGroup>
  );
}

/** The noise suppression "Test microphone" applies, as voice would. */
export function testNoiseMode(): NoiseSuppression {
  return effectiveNoise(useVoice.getState().prefs.noiseSuppression, flagOn('noise_suppression'));
}

/**
 * The priority-speaker key (Discord's "Push to Talk (Priority)"): held,
 * you talk and everyone else in the channel is turned down. Not bound by
 * default.
 */
export function PriorityKeyRow({ conflict }: { conflict?: boolean }) {
  const { t } = useT();
  const key = useVoice((v) => v.prefs.priorityKey);
  const setPrefs = useVoice((v) => v.setPrefs);
  const flag = useFlag('priority_speaker');
  if (!flag) return null;
  const label = t('vx.key.priority');
  return (
    <SetRow label={label} sub={conflict ? t('ns.key.conflict') : t('vx.key.prioritySub')}>
      <span className="ns-combo" data-priority-key="">
        <KeyCapture
          value={key ?? ''}
          label={label}
          onChange={(code) => {
            setPrefs({ priorityKey: code });
            setupInput();
          }}
        />
        {key ? (
          <Btn
            kind="quiet"
            size="sm"
            onClick={() => {
              setPrefs({ priorityKey: null });
              setupInput();
            }}
          >
            {t('ns.key.clear')}
          </Btn>
        ) : null}
      </span>
    </SetRow>
  );
}

/* ---------------------- Server settings › Overview ---------------------- */

/**
 * Discord's Inactive Channel and Inactive Timeout: idle members (no talking,
 * no input) are moved to the AFK channel by the server after the timeout.
 */
export function AfkSettings({ guild }: { guild: Guild }) {
  const { t } = useT();
  const list = useDataShallow((d) =>
    Object.values(d.channels)
      .filter((c) => c.guild_id === guild.id && isVoiceChannelType(c.type))
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
  );
  const flag = useFlag('afk_move');
  const [busy, setBusy] = useState(false);
  const save = async (body: { nx_afk_channel_id?: Snowflake | null; afk_timeout?: number }) => {
    if (busy) return;
    setBusy(true);
    const r = await runAction(() => api.guilds.modify(guild.id, body));
    setBusy(false);
    if (!r.ok) return;
    updateData((d) => ({
      ...d,
      guilds: { ...d.guilds, [guild.id]: { ...d.guilds[guild.id]!, ...r.value } },
    }));
    toast(t('ns.saved'), { icon: 'check', tone: 'ok' });
  };
  const timeout = guild.afk_timeout ?? 300;
  return (
    <div className="vx-afk" data-afk-settings="">
      <h3 className="ns-label">{t('vx.afk.title')}</h3>
      <label className="ns-field">
        <span className="ns-label">{t('vx.afk.channel')}</span>
        <Select
          className="nx-input ns-select"
          value={guild.nx_afk_channel_id ?? ''}
          disabled={busy}
          aria-label={t('vx.afk.channel')}
          onChange={(e) => void save({ nx_afk_channel_id: e.target.value || null })}
        >
          <option value="">{t('vx.afk.none')}</option>
          {list.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name ?? ''}
            </option>
          ))}
        </Select>
      </label>
      <label className="ns-field">
        <span className="ns-label">{t('vx.afk.timeout')}</span>
        <Select
          className="nx-input ns-select"
          value={String(timeout)}
          disabled={busy}
          aria-label={t('vx.afk.timeout')}
          onChange={(e) => void save({ afk_timeout: Number(e.target.value) })}
        >
          {AFK_TIMEOUTS.map((s) => (
            <option key={s} value={String(s)}>
              {s === 3600 ? t('vx.afk.hour') : t('vx.afk.min', { n: s / 60 })}
            </option>
          ))}
        </Select>
      </label>
      <p className="ns-muted">{flag ? t('vx.afk.channelSub') : t('vx.afk.off')}</p>
    </div>
  );
}
